import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { useEditor } from 'components/Canvas';
import CreateModal from './components/CreateModal';
import PreviewModal from '../PreviewModal/Modal';
import ConfirmChangesModal from 'scenes/templateEditor/components/Navbar/components/ConfirmChangesModal';
import TemplateDropwdown from './components/TemplateDropdown/TemplateDropdown';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import {
  isObjectURL,
  remoteImageToFile,
  templateHasChanged,
} from 'helpers/editor';
import { stackSaveErrors } from 'helpers/stacks';
import { textBoxSaveErrors } from 'helpers/textBox';

import './Navbar.scss';

import { Box, Button, useDisclosure } from '@chakra-ui/react';
import Icons from '../Icons';

import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import {
  useCreateDoubleSidedTemplateMutation,
  useUpdateDoubleSidedTemplateByIdMutation,
} from 'modules/templates/templatesApi';

/**
 * The notification for a failed save. A push-down stack or a text box the
 * server rejected is the designer's to fix, so its messages replace the
 * generic one.
 */
const saveFailureMessage = (error, fallback) => {
  const rejected = [
    ['Stack settings', stackSaveErrors(error)],
    ['Text box settings', textBoxSaveErrors(error)],
  ]
    .filter(([, messages]) => messages.length)
    .map(([what, messages]) => `${what} were rejected: ${messages.join('; ')}`);
  return rejected.length ? rejected.join('. ') : fallback;
};

const Navbar = props => {
  const { onTemplateSave } = props;

  const dispatch = useDispatch();
  const editor = useEditor();
  const navigate = useNavigate();
  const location = useLocation();

  const [options, setOptions] = useState({
    createInProgress: false,
    updateInProgress: false,
    requestFail: false,
  });
  const {
    isOpen: isPopoverOpen,
    onOpen: onPopoverOpen,
    onClose: onPopoverClose,
  } = useDisclosure();
  const {
    isOpen: isCreateTemplateModalOpen,
    onOpen: onCreateTemplateModalOpen,
    onClose: onCreateTemplateModalClose,
  } = useDisclosure();
  const {
    isOpen: isConfirmChangesModalOpen,
    onClose: onConfirmChangesModalClose,
    onOpen: onConfirmChangesModalOpen,
  } = useDisclosure();
  const {
    doubleSided,
    currentTemplate,
    setCurrentTemplate,
    setDoubleSidedBgImgUrls,
    sizeFormat,
  } = useTemplateEditorContext();

  const [templateNameEdit, setTemplateNameEdit] = useState(false);

  const [createDoubleSidedTemplate] = useCreateDoubleSidedTemplateMutation();
  const [updateDoubleSidedTemplateById] =
    useUpdateDoubleSidedTemplateByIdMutation();

  const getTemplateLabel = () => {
    if (sizeFormat) {
      const { pixelWidth, pixelHeight } = sizeFormat;
      const width = sizeFormat.isPortrait ? pixelWidth : pixelHeight;
      const height = sizeFormat.isPortrait ? pixelHeight : pixelWidth;
      return (
        <Box sx={{ display: 'flex' }}>
          <Box>{width}px</Box> <Box mx="0.5rem">x</Box> <Box>{height}px</Box>
        </Box>
      );
    }
  };

  const createBackgroundImgFile = async (localTemplate, type) => {
    let invalidFile = false;
    if (doubleSided === false) {
      const url = localTemplate.bg_img?.original_url;
      if (
        (type === 'update' && url !== currentTemplate.bg_img?.original_url) ||
        type === 'create'
      ) {
        if (url && isObjectURL(url)) {
          const fileName = editor.handlers.frameHandler.backgroundFileName;
          let blob = await fetch(url).then(r => r.blob());
          const file = new File([blob], fileName, { type: blob.type });
          localTemplate.bg_img = file;
          invalidFile = !!file.type.includes('webp');
        } else if (url) {
          const file = await remoteImageToFile(url);
          localTemplate.bg_img = file;
          invalidFile = !!file.type.includes('webp');
        } else {
          if (type === 'update') {
            localTemplate.bg_img = '';
          }
        }
      }
    }

    if (doubleSided === true) {
      const localAdTemplatePages = localTemplate.ad_template_pages;
      for (let i = 0; i < localAdTemplatePages.length; i++) {
        const url = localAdTemplatePages[i].bg_img?.original_url;
        const fileName = localAdTemplatePages[i].bg_img?.fileName;
        if (url && isObjectURL(url)) {
          let blob = await fetch(url).then(r => r.blob());
          const file = new File(
            [blob],
            fileName + '.' + blob.type.split('/')[1],
            { type: blob.type }
          );
          localAdTemplatePages[i].bg_img = file;
          invalidFile = !!file.type.includes('webp');
        } else if (url) {
          const file = await remoteImageToFile(url);
          localAdTemplatePages[i].bg_img = file;
          invalidFile = !!file.type.includes('webp');
        }
      }
    }
    return invalidFile;
  };

  const updateTemplateHandler = async status => {
    setOptions({ ...options, updateInProgress: true });
    const localTemplate = editor.handlers.templateHandler.exportToJSON();

    let invalidBackgroundImgFile = await createBackgroundImgFile(
      localTemplate,
      'update'
    );

    // clean up importInit functions so they aren't run on next load of template
    const cleanedVars = localTemplate.ad_template_vars.map(x => {
      if (x.metadata.importInit) {
        delete x.metadata.importInit;
      }
      return x;
    });
    localTemplate.ad_template_vars = cleanedVars;

    if (localTemplate.metadata?.importInit) {
      delete localTemplate.metadata.importInit;
    }

    try {
      const updatedTemplate = await updateDoubleSidedTemplateById({
        id: currentTemplate.id,
        payload: localTemplate,
      }).unwrap();

      // ensure any new entity ids created for template vars are updated into
      // the var objects in the editor
      editor.handlers.templateHandler.setNewEntityIds(updatedTemplate);

      if (doubleSided === false) {
        setDoubleSidedBgImgUrls([
          updatedTemplate?.bg_img?.original_url || '',
          '',
        ]);
      }

      if (doubleSided === true) {
        setDoubleSidedBgImgUrls([
          updatedTemplate?.ad_template_pages?.[0]?.bg_img?.original_url || '',
          updatedTemplate?.ad_template_pages?.[1]?.bg_img?.original_url || '',
        ]);
      }

      if (status !== 'change-save') {
        setCurrentTemplate(updatedTemplate);
      }
      if (
        location.state?.from?.includes('campaigns') ||
        location.state?.from?.includes('analytics')
      ) {
        leaveEditor();
      }
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Update template successfully',
        })
      );
    } catch (error) {
      if (invalidBackgroundImgFile) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Background image format is invalid',
          })
        );
      } else {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: saveFailureMessage(error, 'Failed to update a template'),
          })
        );
      }
    } finally {
      setOptions({ ...options, updateInProgress: false });
    }
  };

  const createTemplateHandler = async name => {
    setOptions({ ...options, createInProgress: true });
    const localTemplate = editor.handlers.templateHandler.exportToJSON();
    const localTemplateWithName = { ...localTemplate, name };

    const invalidBackgroundImgFile = await createBackgroundImgFile(
      localTemplateWithName,
      'create'
    );

    try {
      const createdTemplate = await createDoubleSidedTemplate(
        localTemplateWithName
      ).unwrap();

      // ensure any new entity ids created for template vars are updated into
      // the var objects in the editor
      editor.handlers.templateHandler.setNewEntityIds(createdTemplate);

      if (onTemplateSave) {
        onCreateTemplateModalClose();
        onTemplateSave(createdTemplate);
        setOptions({ ...options, createInProgress: false });
      } else {
        const id = createdTemplate.id;
        if (location.state?.from?.includes('campaigns')) {
          id &&
            navigate(`/template-editor?id=${id}`, {
              from: location.state.from,
              campaignValues: {
                ...location.state.campaignValues,
                adTemplate: id,
              },
            });
        } else {
          id && navigate(`/template-editor?id=${id}`);
        }
        setOptions({ ...options, createInProgress: false });
        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Save template successfully',
          })
        );
        onCreateTemplateModalClose();
      }
    } catch (error) {
      setOptions({ ...options, requestFail: true });
      if (invalidBackgroundImgFile) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Background image format is invalid',
          })
        );
      } else {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: saveFailureMessage(error, 'Fail to crate template'),
          })
        );
      }
    }
  };

  const leaveEditor = () => {
    if (onTemplateSave) {
      onTemplateSave();
    } else {
      if (location.state) {
        if (location.state?.from?.includes('campaigns')) {
          if (location.state.from === 'campaigns-new') {
            navigate('campaigns/new', {
              campaignValues: location.state.campaignValues,
            });
          }
          if (location.search) {
            const searchParams = new URLSearchParams(location.search);
            const ref = searchParams.get('ref');
            navigate(ref, {
              campaignValues: location.state.campaignValues,
            });
          }
        } else {
          navigate(-1);
        }
      } else {
        navigate('/templates');
      }
    }
  };

  const onExit = async () => {
    const localTemplate = editor.handlers.templateHandler.exportToJSON();

    const localTemplateHasChanged = await templateHasChanged(
      currentTemplate,
      localTemplate
    );

    localTemplateHasChanged ? onConfirmChangesModalOpen() : leaveEditor();
  };

  const closeConfirmModal = () => {
    onConfirmChangesModalClose();
    leaveEditor();
  };

  const onChangesSaved = async () => {
    if (currentTemplate) {
      await updateTemplateHandler('change-save');
      closeConfirmModal();
    } else {
      onCreateTemplateModalOpen();
      if (location.state?.from?.includes('campaigns')) {
        leaveEditor();
      }
    }
    onConfirmChangesModalClose();
  };

  return (
    <>
      <CreateModal
        isOpen={isCreateTemplateModalOpen}
        onClose={onCreateTemplateModalClose}
        onSave={createTemplateHandler}
        createInProgress={options.createInProgress}
        requestFail={options.requestFail}
      />
      {isPopoverOpen && (
        <PreviewModal
          isOpen={isPopoverOpen}
          onOpen={onPopoverOpen}
          onClose={onPopoverClose}
        />
      )}

      <ConfirmChangesModal
        isOpen={isConfirmChangesModalOpen}
        onClose={onConfirmChangesModalClose}
        onSave={onChangesSaved}
        onCancel={closeConfirmModal}
      />
      <Box
        className={`template-editor__navbar ${
          templateNameEdit ? 'template-name-edit' : ''
        }`}
        onClick={() => {
          editor.closeKeysMenu();
          editor.closeContextMenu();
        }}
      >
        <Box className="template-editor__navbar-left">
          <Box className="navbar-left__exit" onClick={onExit}>
            <Icons.ChevronLeft size={24} />
            Exit
          </Box>
          <TemplateDropwdown
            setTemplateNameEdit={setTemplateNameEdit}
            updateTemplateHandler={updateTemplateHandler}
            onCreateTemplateModalOpen={onCreateTemplateModalOpen}
          />
        </Box>
        <Box className="template-editor__navbar-center">
          <Box>{getTemplateLabel()}</Box>
        </Box>
        <Box className="template-editor__navbar-right">
          <Button
            className="navbar-right__create-save-btn"
            onClick={() => {
              currentTemplate
                ? updateTemplateHandler()
                : onCreateTemplateModalOpen();
            }}
            variant="variant"
            disabled={options.updateInProgress ? true : false}
          >
            {currentTemplate ? (
              <>{options.updateInProgress ? 'Saving' : 'Save'}</>
            ) : (
              'Create'
            )}
          </Button>
          <Button
            onClick={onPopoverOpen}
            className="navbar-right__preview-btn navbar_preview"
          >
            Preview
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Navbar;
